import { useRef, useState } from "react";
import { Upload, X, FileText, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function FileUploader({ files, setFiles, label = "Documents", testId = "file-uploader" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (selected) => {
    if (!selected || selected.length === 0) return;
    setUploading(true);
    for (const f of selected) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) => [...prev, data]);
        toast.success(`Uploaded ${data.original_filename}`);
      } catch (err) {
        toast.error(`Failed to upload ${f.name}`);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (id) => setFiles((prev) => prev.filter((x) => x.id !== id));

  return (
    <div data-testid={testId}>
      <label className="block text-sm font-medium text-forest mb-2">{label}</label>
      <div
        onClick={() => inputRef.current?.click()}
        className="border border-dashed border-line bg-cream/40 hover:bg-cream rounded-sm p-8 text-center cursor-pointer transition-colors duration-200"
        data-testid={`${testId}-dropzone`}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2 text-sage">
            <Loader2 className="w-6 h-6 animate-spin text-clay" />
            <span className="text-sm">Uploading…</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-sage">
            <Upload className="w-6 h-6 text-clay" strokeWidth={1.5} />
            <span className="text-sm font-medium text-forest">Click to upload</span>
            <span className="text-xs">PDF, PNG, JPG, DOC up to 10MB</span>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.txt"
          className="hidden"
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
          data-testid={`${testId}-input`}
        />
      </div>

      {files.length > 0 && (
        <ul className="mt-4 space-y-2">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex items-center justify-between bg-white border border-line rounded-sm px-4 py-3"
              data-testid={`${testId}-file-${f.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-4 h-4 text-clay flex-shrink-0" />
                <span className="text-sm text-forest truncate">{f.original_filename}</span>
                <span className="text-xs text-sage flex-shrink-0">
                  {(f.size / 1024).toFixed(1)} KB
                </span>
              </div>
              <button
                type="button"
                onClick={() => remove(f.id)}
                className="text-sage hover:text-danger transition-colors duration-200"
                data-testid={`${testId}-remove-${f.id}`}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
